import { Box } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

import {
  FinancialData,
  Organization,
  Util,
} from '@finley-technologies/finley-types';

import { GetFinancialStatementDataByDateRangeQueryGQLType } from '../../requests/financialData.gql.generated';
import { generateFinancialStatementsChartConfig } from './financialStatementsChartConfigs';
import { colors } from '~/packages/common-frontend/colors';
import { formatFinancialValue } from '~/packages/financial-data/utils/financialStatementFormatting';
import {
  ChartTimePeriod,
  getInitialActiveFields,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioReChart } from '~/packages/portfolio/ui/shared/PortfolioReChart';

type FinancialStatementChartProps = {
  creditFacilityId: string;
  selectedSectionName:
    | FinancialData.BalanceSheetSectionTypes
    | FinancialData.IncomeStatementSectionTypes;
  reportFrequency: FinancialData.ReportFrequency;
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
  financialStatementData?: GetFinancialStatementDataByDateRangeQueryGQLType['getFinancialStatementDataByDateRange'];
  financialStatementConfig?: Organization.FinancialStatementLocalizationConfig;
};
export const FinancialStatementsChart = ({
  creditFacilityId,
  selectedSectionName,
  reportFrequency,
  startDate,
  endDate,
  financialStatementData,
  financialStatementConfig,
}: FinancialStatementChartProps) => {
  const [lineItemsDescriptions, setLineItemsDescriptions] = useState<string[]>(
    [],
  );

  const [sectionKeysToLineItems, setSectionKeysToLineItems] = useState<
    NonNullable<
      GetFinancialStatementDataByDateRangeQueryGQLType['getFinancialStatementDataByDateRange']
    >['sectionKeysToLineItems']
  >([]);

  const [sortedEndOfPeriodDates, setSortedEndOfPeriodDates] = useState<
    Util.FullDateStr[]
  >([]);

  const sectionData = sectionKeysToLineItems.find(
    (sectionKeyToLineItems) =>
      sectionKeyToLineItems.finleySectionKey === selectedSectionName,
  );

  const [
    financialStatementChartData,
    setFinancialStatementChartData,
  ] = useState<FinancialData.FinancialStatementsChartDataType[]>([]);

  const initialChartConfig = generateFinancialStatementsChartConfig({
    lineItemsDescriptions,
    lineItems: sectionData
      ? sectionData.lineItemsToValues.map(
          (lineItemToValues) => lineItemToValues.lineItemDescription,
        )
      : [],
  });

  const initialActiveFields = getInitialActiveFields({
    chartConfig: initialChartConfig,
  }) as (keyof FinancialData.FinancialStatementsChartDataType)[];

  // Create custom tooltip formatter that uses localization config
  const customTooltipFormatter = financialStatementConfig
    ? (value: number, name: string): [string, string] => {
        const formattedValue = formatFinancialValue(
          value,
          financialStatementConfig,
        );
        return [formattedValue, name];
      }
    : undefined;

  useEffect(() => {
    if (financialStatementData) {
      setSectionKeysToLineItems(financialStatementData.sectionKeysToLineItems);
      setSortedEndOfPeriodDates(
        financialStatementData.metadata.sortedEndOfPeriodDates,
      );
    } else {
      setFinancialStatementChartData([]);
      setSectionKeysToLineItems([]);
    }
  }, [financialStatementData]);

  useEffect(() => {
    const newFinancialStatementChartData: FinancialData.FinancialStatementsChartDataType[] = [];
    const newLineItemDescriptions: string[] = [];

    if (sectionData) {
      sectionData.lineItemsToValues.forEach((lineItemToValues) => {
        newLineItemDescriptions.push(lineItemToValues.lineItemDescription);
        lineItemToValues.values.forEach((value, idx) => {
          if (idx >= newFinancialStatementChartData.length) {
            newFinancialStatementChartData.push({
              reportingDate: sortedEndOfPeriodDates[idx],
            });
          }

          newFinancialStatementChartData[idx][
            lineItemToValues.lineItemDescription
          ] = value.value ?? 0;
        });
      });
    }

    setLineItemsDescriptions(newLineItemDescriptions);
    setFinancialStatementChartData(newFinancialStatementChartData);
  }, [
    creditFacilityId,
    sectionData,
    sortedEndOfPeriodDates,
    reportFrequency,
    startDate,
    endDate,
  ]);

  return financialStatementChartData.length > 0 ? (
    <Box
      backgroundColor="white"
      borderRadius="8px"
      borderWidth="1px"
      borderColor={colors.gray[200]}
      padding="40px"
      my="20px"
    >
      <PortfolioReChart
        chartData={financialStatementChartData}
        selectedTimePeriod={reportFrequency as ChartTimePeriod}
        currentSelectedBarReportingDate={'2024-03-05' as Util.FullDateStr} // date is not at the end of month, so no bar is selected @haghanim
        onSelectBar={() => reportFrequency as ChartTimePeriod}
        chartConfig={initialChartConfig}
        visibleChartComponents={initialActiveFields}
        leftYAxisDomain={[0, 'auto']}
        customTooltipFormatter={customTooltipFormatter}
      />
    </Box>
  ) : (
    <></>
  );
};
